import { useCallback, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon24Close } from '@figma/fpl-icons';
import fplTokens from '@figma/fpl-tokens/css-vars';
import { TextLgMd, TextSmRg, TextStdMd } from '@/UI/AppTypography/AppTypography';
import { Box } from '@/UI/Box/Box';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { FloatingPanel, type FloatingPanelProps } from '@/UI/FloatingPanel/FloatingPanel';
import { I18N_KEYS } from '@/language/keys';
import type { TourStep } from '../tourTypes';

const COACHMARK_WIDTH = 240;
const THUMBNAIL_HEIGHT = 135;
const ROW_HEIGHT = 40;
const TEXT_HEIGHT = 64;
// Figma measures the gap from the Tool panel's edge, so the steps anchor to full-width panel
// sections rather than to the control that opens them. The tail protrudes into this gap.
const PANEL_GAP = 12;
// Keeps the tail off the surface's rounded corners.
const ARROW_PADDING = 12;

export type ToolBuilderCoachmarkMediaCrop = NonNullable<TourStep['mediaCrop']>;

interface ToolBuilderTourCoachmarkProps {
  anchorEl: HTMLElement | null;
  content: string;
  mediaCrop?: ToolBuilderCoachmarkMediaCrop;
  mediaSrc: string;
  onAbandon: () => void;
  onDismiss: () => void;
  open: boolean;
  placement?: FloatingPanelProps['placement'];
  smallTitle?: boolean;
  /** Footer button. Defaults to "Got it". */
  actionLabel?: string;
  title: string;
}

const IMAGE_STYLES = {
  fill: { height: '100%', objectFit: 'cover', top: 0 },
  inputs: { height: '241.42%', objectFit: 'fill', top: '-35.68%' },
  preview: { height: '266.45%', objectFit: 'fill', top: '-133.07%' },
} as const satisfies Record<ToolBuilderCoachmarkMediaCrop, CSSProperties>;

/** Feature-education popover (Direction=Right, Thumbnail=True) used by the Tool-builder tour. */
export function ToolBuilderTourCoachmark({
  anchorEl,
  content,
  mediaCrop = 'fill',
  mediaSrc,
  onAbandon,
  actionLabel,
  onDismiss,
  open,
  placement = 'left-start',
  smallTitle = false,
  title,
}: ToolBuilderTourCoachmarkProps) {
  const { t } = useTranslation();
  const Title = smallTitle ? TextStdMd : TextLgMd;
  const focusSurface = useCallback(
    (element: HTMLDivElement | null) => {
      if (open) {
        element?.focus();
      }
    },
    [open],
  );

  return (
    // No `onClose`, so an outside click leaves the coachmark open. "Got it" spends this step; the X
    // abandons the entire remaining tour.
    <FloatingPanel
      anchorEl={anchorEl}
      arrowPadding={ARROW_PADDING}
      offset={PANEL_GAP}
      open={open}
      placement={placement}
      showArrow
      stayOnSide
      sx={{ gap: 0, overflow: 'hidden', padding: 0, width: COACHMARK_WIDTH }}
    >
      <Box
        ref={focusSurface}
        aria-label={title}
        role="dialog"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}
        tabIndex={-1}
      >
        <Box
          data-testid="tool-builder-coachmark-surface"
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: ROW_HEIGHT,
            justifyContent: 'space-between',
            pl: 2,
            pr: 1,
            width: '100%',
          }}
        >
          {/* Tool-builder titles use 13px strong; compact education titles use 11px strong. */}
          <Title sx={{ color: fplTokens.color.text }}>{title}</Title>
          <AppIconButton aria-label={t(I18N_KEYS.GENERAL.CLOSE)} height={24} onClick={onAbandon} width={24}>
            <Icon24Close aria-hidden />
          </AppIconButton>
        </Box>
        <Box sx={{ backgroundColor: fplTokens.color.border, height: '1px', width: '100%' }} />
        <Box
          data-testid="tool-builder-coachmark-media"
          sx={{
            height: THUMBNAIL_HEIGHT,
            overflow: 'hidden',
            position: 'relative',
            width: COACHMARK_WIDTH,
          }}
        >
          <Box
            alt=""
            component="img"
            src={mediaSrc}
            sx={{
              display: 'block',
              left: 0,
              maxWidth: 'none',
              pointerEvents: 'none',
              position: 'absolute',
              width: '100%',
              ...IMAGE_STYLES[mediaCrop],
            }}
          />
        </Box>
        <Box sx={{ boxSizing: 'border-box', minHeight: TEXT_HEIGHT, px: 2, py: 1 }}>
          <TextSmRg sx={{ color: fplTokens.color.text, whiteSpace: 'pre-wrap' }}>{content}</TextSmRg>
        </Box>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: ROW_HEIGHT,
            justifyContent: 'flex-end',
            pr: 1,
            width: '100%',
          }}
        >
          <ButtonContained mode="filled-light" onClick={onDismiss} size="xs">
            {actionLabel ?? t(I18N_KEYS.PRODUCT_TOURS.FLOW.TOOL_BUILDER_TOUR.CTA_GOT_IT)}
          </ButtonContained>
        </Box>
      </Box>
    </FloatingPanel>
  );
}
